/* eslint-disable react/prop-types */
import React, { useState, FC, HTMLAttributes, SyntheticEvent } from 'react'
import { useParams } from 'react-router'
import { useRecoilValue, useRecoilState } from 'recoil'
import { IconButton, Box, Flex, Heading, Skeleton } from '@chakra-ui/core'

import { Hook, IStorefrontSettings } from '@engyalo/storefront-types'
import { searchStarted } from '@engyalo/storefront-events'
import {
  IProductPricedPerCollectionLevel1,
  IProductsPricedPerCollectionCollection,
} from '@engyalo/storefront-types/lib/graphql/product'
import { getStoreFront_orderConfig as IOrderConfig } from '@engyalo/storefront-types/lib/graphql/storefront'
import { ISearchStats, IConfig } from '@engyalo/storefront-types/lib/plugins/model'
import { ProductList, ProductListFrom } from '../../components/ProductList'
import { Searchbar } from '../../components/Searchbar'
import { CartIcon } from '../../components/CartIcon'
import { CategoriesMenu } from '../../components/CategoriesMenu'
import { configState, orderConfigState, searchStats } from '../../recoil/atoms'
import { notEmpty } from '../../lib/utilities'
import { skipIfInPreviewMode } from '../../utils/mode'
import { usePlugin } from '../../Plugin'
import { ButtonTotal } from '../../components/ButtonTotal'
import { LinkButton } from '../../lib/types'
import { collectionsWithCustomization } from '../../utils/__fixtures__/preloaded-cart'
import Error from '../Error'
import { ErrorTypes } from '../../lib/constants'

const DEFAULT_LOGO_URL = 'https://storage.googleapis.com/yalo-img/4k/logo-purple.png'

interface IProps extends HTMLAttributes<HTMLDivElement> {
  loading: boolean
  products: (IProductsPricedPerCollectionCollection | null)[]
}

export const Catalogue: FC<IProps> = ({ loading, products }: IProps) => {
  const [searchbarStatus, setSearchbarStatus] = useState(false)
  const config = useRecoilValue<IConfig>(configState)
  const orderConfig = useRecoilValue<IOrderConfig>(orderConfigState)
  const [currentSearchStats, setSearchStats] = useRecoilState<ISearchStats>(searchStats)
  const storefrontPlugin = usePlugin(Hook.STOREFRONT)
  const hidePrices = (storefrontPlugin?.props as IStorefrontSettings)?.hidePrices ?? false
  const [verticalHeight, setVerticalHeight] = useState(window.innerHeight * 0.01)
  const showCategoriesView = (storefrontPlugin?.props as IStorefrontSettings)?.showCategoriesView ?? false
  const { categoryid, customer, order } = useParams()

  const addDefaultSrc = (e: SyntheticEvent<HTMLImageElement, Event>): void => {
    e.currentTarget.src = DEFAULT_LOGO_URL
  }

  const getProducts = (): (IProductsPricedPerCollectionCollection | null)[] => {
    if (categoryid) {
      return products.filter((collection) => collection?.id?.toString() === categoryid)
    } else {
      return products
    }
  }

  const renderProduct =
    products.length === 0 ? (
      <Box textAlign="center" transform={'translateY(165%)'}>
        <Error type={ErrorTypes.TTL_ERROR} />
      </Box>
    ) : (
      getProducts().map((collection) => {
        if (collection && collection.productsPriced && collection.name) {
          const notEmptyProducts: IProductPricedPerCollectionLevel1[] = collection.productsPriced.filter(notEmpty)
          const productsPriced = hidePrices
            ? notEmptyProducts.map((product: IProductPricedPerCollectionLevel1) => ({
                ...product,
                price: 0,
              }))
            : notEmptyProducts

          let showPrice = !hidePrices
          if (collection.enabledPrice !== null && collection.enabledPrice !== undefined) {
            showPrice = collection.enabledPrice
          }
          return (
            <div id={collection.name} key={collection.name}>
              <Heading as="h3" fontSize="category" padding="0.8rem 0 0.4rem 1.2rem" fontWeight="500">
                {collection.name}
              </Heading>
              <ProductList
                products={productsPriced}
                from={ProductListFrom.COLLECTIONS}
                showPrice={showPrice}
                useVirtualizedList={!!categoryid}
              />
            </div>
          )
        }

        return null
      })
    )
  return (
    <Flex h="100%" w="100%" flexDirection="column">
      {searchbarStatus && <Searchbar status={searchbarStatus} closeAction={(): void => setSearchbarStatus(false)} />}
      <Flex
        w="100%"
        maxW="42.5rem"
        justifyContent="space-between"
        padding="0.8rem 0.8rem 0 0.8rem"
        position="sticky"
        top={0}
        flex="0 0 auto"
        zIndex={2}
        background="white"
        flexDir="column"
      >
        {!searchbarStatus && (
          <Flex
            h="40px"
            w="100%"
            maxW="42.5rem"
            justifyContent="space-between"
            padding="0.8rem 0.8rem 0 0.8rem"
            flex="0 0 auto"
          >
            {loading ? (
              <Skeleton m=".8rem" height="1.6rem" width="1.6rem" />
            ) : (
              <>
                {categoryid && (
                  <LinkButton
                    to={{ pathname: `/${customer}/${order}` }}
                    h="100%"
                    w="3.2rem"
                    background="transparent"
                    borderColor="transparent"
                    size="lg"
                    aria-label="cart"
                    icon="chevron-left"
                    color="primary.500"
                  />
                )}
                <img onError={addDefaultSrc} alt="logo" height="100%" src={config.logo} />
              </>
            )}
            <Flex>
              <IconButton
                h="100%"
                w="3.2rem"
                background="transparent"
                aria-label="search"
                size="lg"
                icon="search"
                onClick={(): void => {
                  const newSearchOpenCount = currentSearchStats.openCount + 1
                  skipIfInPreviewMode(() => {
                    searchStarted({
                      stats: {
                        totalSearchStart: newSearchOpenCount,
                      },
                      session: {
                        userID: orderConfig.userID || '',
                        customer: config.customer,
                        sessionID: orderConfig.id || '',
                      },
                    })
                  })
                  setSearchStats({
                    ...currentSearchStats,
                    openCount: newSearchOpenCount,
                  })
                  setSearchbarStatus(true)
                }}
                borderStyle="none"
                borderColor="unset"
              />
              <CartIcon />
            </Flex>
          </Flex>
        )}
        {!categoryid && <CategoriesMenu loading={loading} products={products} showVerticalMenu={showCategoriesView} />}
      </Flex>
      {searchbarStatus && (
        <Box
          position="absolute"
          opacity={0.5}
          backgroundColor="black"
          width={['100%', '425px']}
          height={['100%', '720px']}
          zIndex={50}
        />
      )}
      {(!showCategoriesView || (showCategoriesView && categoryid)) && (
        <Box flex="1" id="containerElement" paddingBottom="2rem" overflowY={categoryid ? 'hidden' : 'scroll'}>
          {loading
            ? [1, 2, 3].map((number) => (
                <Box key={number}>
                  <Skeleton height="1.8rem" width="6rem" margin="1.2rem" />

                  <ProductList products={Array(5).fill({})} from={ProductListFrom.COLLECTIONS} isLoading={loading} />
                </Box>
              ))
            : renderProduct}
        </Box>
      )}
      <ButtonTotal />
    </Flex>
  )
}
